/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import { makeStyles } from '@material-ui/core';
import {
    bool,
    func, object, string,
} from 'prop-types';
import ReactHTMLParser from 'react-html-parser';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import mbpLogger from '@module-logger/mbp';
import { getPriceArrays } from '../../../../../../../../helpers/DynamicPricing/getPriceArrays';
import PriceRange from './PriceRange';
import { transformCharacters } from '../../../../../../../../helpers/markdown/characterTransformer';
import PriceShownSkeleton from '../../../../../../GraphqlSkeletonComponents/PriceShownSkeleton';
import { GRAPHQL_ENV } from '../../../../../../../../gql';
import findProductPageByPartNumber from '../../../../../../../../gql/queries/findProductPageByPartNumber';
import { getBrand } from '../../../../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getFeatureFlag } from '../../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const useStyles = makeStyles(() => ({
    dialog: {
        display: 'flex',
        justifyContent: 'center',
        width: '384px',
        marginTop: '5px',
        '@media screen and (max-width: 384px)': {
            width: '98%',
            paddingLeft: '16px',
        },
    },
    dialogWrapper: {
        background: 'white',
        borderRadius: '5px',
        boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.30);',
        margin: '0 auto',
        height: 'max-content',
    },
    dialogHeader: {
        justifyContent: 'space-between',
        width: '100%',
        gridTemplateColumns: 'calc(100% - 16px) 16px',
        display: 'grid',
        padding: '8px 8px 8px 20px',
        backgroundColor: '#E9E9E9',
        alignItems: 'center',
        borderRadius: '5px',
        borderBottomRightRadius: '0px',
        borderBottomLeftRadius: '0px',
    },
    closeIcon: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
    },
    dialogContent: {
        padding: '16px 20px',
        gridTemplateColumns: 'minmax(min-content, 275px) minmax(min-content, 1fr)',
        display: 'grid',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        '& div': {
            fontSize: '16px',
            lineHeight: '26px',
            letterSpacing: '0.16px',
            fontWeight: '400',
            color: '#000000',
            '@media screen and (max-width: 400px)': {
                fontSize: '14px',
            },
            '@media screen and (max-width: 362px)': {
                fontSize: '12px',
            },
        },
    },
    title: {
        fontSize: '14px',
        lineHeight: '26px',
        letterSpacing: '0.5px',
        width: 'auto',
    },
    dialogMiddle: {
        borderTop: '1px solid #C4C4C4',
        margin: '0px 20px 12px',
        paddingTop: '12px',
        gridTemplateColumns: '50%  auto',
        display: 'grid',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: 'auto',
        '& div': {
            fontSize: '16px',
            lineHeight: '26px',
            letterSpacing: '0.5px',
            fontFamily: 'museo-sans, "Area Variable", LatoBold',
            fontWeight: '700',
            '@media screen and (max-width: 400px)': {
                fontSize: '14px',
            },
            '@media screen and (max-width: 362px)': {
                fontSize: '12px',
            },
        },
    },
    priceContent: {
        display: 'flex',
        justifyContent: 'flex-end',
        textAlign: 'end',
        fontWeight: 'bold !important',
    },
}));

const PriceShownDetail = ({
    skuPriceRange, openPriceDialog, partNumber, displayPrice,
}) => {
    const brand = useSelector(getBrand);
    const ffPdSkuRankingEnabled = useSelector(getFeatureFlag('is-pdp-sku-ranking-enabled'));
    const classes = useStyles();
    const FIND_PDP_BY_PART = findProductPageByPartNumber(brand.domain, partNumber);
    const { loading, error, data } = useQuery(FIND_PDP_BY_PART);

    if (loading) return <PriceShownSkeleton />;

    if (error) {
        mbpLogger.logWarning({
            appName: process.env.npm_package_name,
            query: FIND_PDP_BY_PART,
            component: 'PriceShownUi.js',
            message: 'Error in query',
            env: GRAPHQL_ENV,
        });
        return null;
    }
    if (!data) {
        mbpLogger.logWarning({
            appName: process.env.npm_package_name,
            query: FIND_PDP_BY_PART,
            component: 'PriceShownUi.js',
            message: 'No data returned for query',
            env: GRAPHQL_ENV,
        });
        return null;
    }
    const productSkus = data?.findProductPageByPartNumber?.product?.productSkus || [];
    const brandId = data?.findProductPageByPartNumber?.product?.brandId;
    const skusHaveDuplicateRanksOrAreUnranked = (skus) => {
        const ranks = new Set();
        return skus.some((sku) => {
            if (sku.rank === 99) {
                return true;
            }
            return ranks.size === ranks.add(sku.rank).size;
        });
    };
    const sku_ranking = data?.content?.entries?.[0]?.sku_section?.sku_ranking;
    let sortedSku = null;
    if (ffPdSkuRankingEnabled && !skusHaveDuplicateRanksOrAreUnranked(productSkus) && brandId !== '1030') {
        sortedSku = productSkus
            .slice(0)
            .sort(
                (a, b) => Number(a.rank) - Number(b.rank),
            );
    } else if (sku_ranking === 'low to high') {
        sortedSku = productSkus
            .slice(0)
            .sort(
                (a, b) => Number(a.prices?.[0]?.value) - Number(b.prices?.[0]?.value),
            );
    } else {
        sortedSku = productSkus
            .slice(0)
            .sort(
                (a, b) => Number(b.prices?.[0]?.value) - Number(a.prices?.[0]?.value),
            );
    }

    const defaultSku = sortedSku?.[0]  || {};
    const productId = defaultSku?.id;

    const exists = skuPriceRange?.sale?.some?.((i) => i.skuPartNumber === productId)
        || skuPriceRange?.retail?.some?.((i) => i.skuPartNumber === productId);
    let skuPrice = {};
    if (exists) {
        skuPrice = ({
            sale: skuPriceRange?.sale?.filter?.((item) => item.skuPartNumber === productId),
            retail: skuPriceRange?.retail?.filter?.((item) => item.skuPartNumber === productId),
        });
    } else {
        skuPrice = defaultSku?.prices?.reduce?.((acc, curr) => {
            if (curr.type === 'sale' || curr.type === 'retail') {
                acc[curr.type] = [curr];
            }
            return acc;
        }, {});
    }

    const closeIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M3.52925 4.25788C3.7896 3.99753 4.21171 3.99753 4.47206 4.25788L8.00065 7.78648L11.5292 4.25788C11.7896 3.99753 12.2117 3.99753 12.4721 4.25788C12.7324 4.51823 12.7324 4.94034 12.4721 5.20069L8.94346 8.72929L12.4721 12.2579C12.7324 12.5182 12.7324 12.9403 12.4721 13.2007C12.2117 13.461 11.7896 13.461 11.5292 13.2007L8.00065 9.6721L4.47206 13.2007C4.21171 13.461 3.7896 13.461 3.52925 13.2007C3.2689 12.9403 3.2689 12.5182 3.52925 12.2579L7.05784 8.72929L3.52925 5.20069C3.2689 4.94034 3.2689 4.51823 3.52925 4.25788Z" fill="black" />
        </svg>
    );
    return (
        <div className={classes.dialog}>
            <div className={classes.dialogWrapper}>
                <div className={classes.dialogHeader}>
                    <div className={classes.title}>
                        <strong>Price as shown</strong>
                    </div>
                    <div role="presentation" className={classes.closeIcon} aria-label="close" onClick={() => openPriceDialog()}>
                        {closeIcon()}
                    </div>
                </div>
                <div className={classes.dialogContent}>
                    <div>{transformCharacters(ReactHTMLParser(defaultSku?.name))}</div>
                    <div className={classes.priceContent}>
                        <PriceRange
                            skuPriceRange={skuPrice}
                            displayPrice={displayPrice}
                            noFromText
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

PriceShownDetail.propTypes = {
    skuPriceRange: object.isRequired,
    openPriceDialog: func.isRequired,
    partNumber: string.isRequired,
    displayPrice: bool.isRequired,
};
export default PriceShownDetail;
